"use client";

import * as React from "react";
import {
  motion,
  useMotionValue,
  useReducedMotion,
  useSpring,
} from "framer-motion";
import { cn } from "@/lib/cn";

type Variant = "primary" | "secondary" | "ghost";
type Size = "sm" | "md" | "lg";

// Nomes literais (o Tailwind não enxerga classes montadas com template string)
const variantClass: Record<Variant, string> = {
  primary: "btn-primary",
  secondary: "btn-secondary",
  ghost: "btn-ghost",
};
const sizeClass: Record<Size, string> = {
  sm: "btn-sm",
  md: "btn-md",
  lg: "btn-lg",
};

type CommonProps = {
  variant?: Variant;
  size?: Size;
  /** Efeito magnético (padrão: ligado apenas no botão primário) */
  magnetic?: boolean;
  /** Ocupa toda a largura do contêiner */
  fullWidth?: boolean;
  icon?: React.ReactNode;
  trailing?: React.ReactNode;
  className?: string;
  children: React.ReactNode;
};

type AnchorProps = CommonProps & { href: string } & Omit<
    React.AnchorHTMLAttributes<HTMLAnchorElement>,
    keyof CommonProps
  >;
type NativeButtonProps = CommonProps & { href?: undefined } & Omit<
    React.ButtonHTMLAttributes<HTMLButtonElement>,
    keyof CommonProps
  >;

export type ButtonProps = AnchorProps | NativeButtonProps;

export default function Button(props: ButtonProps) {
  const {
    variant = "primary",
    size = "md",
    magnetic,
    fullWidth,
    icon,
    trailing,
    className,
    children,
    ...rest
  } = props;

  const reduce = useReducedMotion();
  const x = useMotionValue(0);
  const y = useMotionValue(0);
  const sx = useSpring(x, { stiffness: 260, damping: 18, mass: 0.4 });
  const sy = useSpring(y, { stiffness: 260, damping: 18, mass: 0.4 });
  const enabled = (magnetic ?? variant === "primary") && !reduce;

  const onMove = (e: React.PointerEvent<HTMLSpanElement>) => {
    if (!enabled || e.pointerType !== "mouse") return;
    const r = e.currentTarget.getBoundingClientRect();
    x.set((e.clientX - (r.left + r.width / 2)) * 0.16);
    y.set((e.clientY - (r.top + r.height / 2)) * 0.26);
  };
  const reset = () => {
    x.set(0);
    y.set(0);
  };

  const classes = cn(
    "btn",
    variantClass[variant],
    sizeClass[size],
    fullWidth && "w-full",
    className
  );
  const content = (
    <>
      {icon}
      <span>{children}</span>
      {trailing}
    </>
  );

  return (
    <motion.span
      style={{ x: sx, y: sy }}
      onPointerMove={onMove}
      onPointerLeave={reset}
      className={fullWidth ? "flex w-full" : "inline-flex"}
    >
      {"href" in rest && rest.href !== undefined ? (
        <a
          className={classes}
          {...(rest as React.AnchorHTMLAttributes<HTMLAnchorElement>)}
        >
          {content}
        </a>
      ) : (
        <button
          type="button"
          className={classes}
          {...(rest as React.ButtonHTMLAttributes<HTMLButtonElement>)}
        >
          {content}
        </button>
      )}
    </motion.span>
  );
}
